import type { AdminUserRole, AlwaysAiUserRole, BookingUserRole } from "./schema.js";

// NAMED PERMISSIONS (NO-405).
//
// A role name answers "who is this person on the org chart". A permission
// answers "what may they do". Every guard in both apps used to ask the first
// question and treat the answer as the second — `role === "admin"` at three
// dozen route-layer branch points — so the only way to learn what `supervisor`
// may do was to read all of them, and adding a role meant finding every one.
// Here the answer is declared once: a flat vocabulary of permissions, and a map
// from each app's roles to the permissions that role holds.
//
// NOTHING HERE IS STORED. `core_data.user_roles` still holds ROLES, one row per
// (user, app, role), and a role is still what an admin grants. Permissions are
// derived from the role at request time, which is why this file has no table and
// no migration: changing what a role may do is a code change that ships with the
// guards it changes, and the audit trail in `role_events` keeps naming the role
// that was granted rather than a set that would go stale the moment this map
// moved.
//
// THE SCHEME IS `resource:verb` or `resource:verb:scope`.
//
//   resource — the thing being reached (`sessions`, `rulesets`, `staff`)
//   verb     — what is being done to it (`read`, `manage`, `run`)
//   scope    — WHICH ROWS, and present only when the answer is "some of them"
//
// A permission with no scope segment is not row-scoped at all: `rulesets:manage`
// is the whole ruleset surface or none of it. A permission WITH one names a row
// predicate the guard still has to evaluate in SQL — the scope segment picks
// WHICH predicate, it does not replace it. `sessions:read:supervised` means "the
// sessions of the RDs you supervise", and the join that decides which RDs those
// are is still `core_data.providers.supervised_by_user_id`.
//
// Four scopes exist today, and a caller holds at most one rung of a given
// `resource:verb` ladder: `all` (every row), `supervised` (rows whose
// supervision edge names the caller), and `mapped` (rows that have a supervisor
// at all — the uploader tier, which files work for somebody else to review).
// `own` follows the current user's active Provider — to Sessions whose `rd_id`
// names that Provider, to the Appointments and Appointment Notes that Provider
// owns, to the Patient Quick Note of a Patient on one of those Appointments,
// and to that Patient's open Red Flag roll-up, built only from Sessions whose
// `rd_id` names that Provider. Patient and Appointment links do not participate
// in reaching a Session: only the `rd_id` edge does. Guards read ladders widest-first.
//
// The appointment rungs both resolve one `(Provider, owning User)` pair and share
// one SQL predicate, differing only in whether the caller or an explicitly
// selected RD supplies it — so `all` widens who may name a target, not what a row
// must satisfy (NO-467).

// The whole vocabulary, grouped by the app whose guards read it. NDD Admin is
// the only app that grants `staff:manage`; product apps retain no staff route.
//
// Every name below is the gate on a route that exists today, with one exception:
// `notification_defaults:manage:all` (NO-827) ships ahead of the notification
// settings routes it gates, which land in NO-829. The `rd_hours:manage` ladder
// gates both AlwaysAI's RD hours routes (NO-867) and its RD blocked time routes
// (NO-871: breaks, project time and personal time at
// `/api/rd-schedule/blocks`), with the same `own`, `supervised` and `all`
// reach. The `appointments:manage` ladder (NO-872) is the one appointment write
// rung; it gates only AlwaysAI's RD Schedule action routes under
// /api/rd-schedule, and opens no Note, presence or Enhancement write. Scoped
// ladders use
// `all`, `supervised`, `own`, and `mapped`: admins reach every RD, supervisors
// reach their roster, RD users read Sessions owned by their Provider identity,
// and uploader roles may file work against an RD that has a supervisor.
export const PERMISSIONS = [
  // --- NDD Booking ---------------------------------------------------------
  /** GET /api/admin/health/deep — the dependency-probing health report. */
  "platform_health:read",
  /** /api/admin/coverage — which states have bookable provider coverage. */
  "state_coverage:read",
  /** /api/admin/hubspot — the HubSpot owner/pipeline directory lookup. */
  "hubspot_directory:read",
  /** /api/admin/rds — the schedulable-RD roster and its overrides. */
  "schedulable_rds:manage",
  /** /api/admin/appointment-types — the Healthie appointment-type catalog. */
  "appointment_types:manage",
  /** /api/admin/rulesets — booking's scheduling rulesets. */
  "rulesets:manage",
  /** /api/admin/analysis-chats — the booking-data analysis chat surface. */
  "analysis_chats:use",
  /** /api/admin/call-review/hubspot — the HubSpot call-review lane. */
  "call_review:use",
  /**
   * POST /api/admin/rds/sync-from-healthie and its readiness precheck — the
   * controls that pull Healthie's reference data into booking.
   */
  "reference_sync:manage",
  /** POST /api/admin/telemetry/sentry-smoke/server — the Sentry smoke probe. */
  "telemetry_smoke:run",

  // --- always-ai -----------------------------------------------------------
  /** Reach any RD's workspace: its sessions, notes, forms, and patient panel. */
  "rds:read:all",
  /** Reach the workspaces of the RDs whose supervision edge names you. */
  "rds:read:supervised",
  /** Read metrics across the RD roster. */
  "metrics:read:all",
  /** Read metrics for RDs whose current supervision edge names you. */
  "metrics:read:supervised",
  /** Create an RD under any eligible supervisor. */
  "rds:create:all",
  /** Create an RD under yourself, and only yourself. */
  "rds:create:supervised",
  /** Create an RD only by naming the supervisor it will answer to. */
  "rds:create:mapped",
  /** Read any session. */
  "sessions:read:all",
  /** Read the sessions of the RDs you supervise. */
  "sessions:read:supervised",
  /** Read Sessions whose RD identity is your active Provider. */
  "sessions:read:own",
  /** Read Appointments owned by the active Provider of any accessible RD. */
  "appointments:read:all",
  /** Read Appointments owned by the signed-in RD's active Provider. */
  "appointments:read:own",
  /** Read Session Recaps for the signed-in RD. */
  "session_recaps:read:own",
  /** Read Appointment Notes for any accessible RD's owned Appointments. */
  "appointment_notes:read:all",
  /** Read Appointment Notes for Appointments owned by the active Provider. */
  "appointment_notes:read:own",
  /** Append Appointment Note Revisions for owned Appointments. */
  "appointment_notes:write:own",
  /** Read the Patient Quick Note through an Appointment the active Provider owns. */
  "patient_quick_notes:read:own",
  /** Append Patient Quick Note Revisions through an owned Appointment. */
  "patient_quick_notes:write:own",
  /**
   * Read a Client's open critical clinical Red Flag (NO-846): the newest
   * critical clinical flag from the Client's Session Analyses the active
   * Provider owns. Narrower than `session_analysis:read`: it opens only that
   * roll-up, never the Analysis itself.
   */
  "patient_red_flags:read:own",
  /** Record that the RD called the Client about that open Red Flag (NO-846). */
  "patient_red_flags:write:own",
  /** Read SOAP Drafts owned by the signed-in RD's active Provider. */
  "soap_drafts:read:own",
  /** Append SOAP Draft Versions owned by the signed-in RD's active Provider. */
  "soap_drafts:edit:own",
  /** Approve an exact SOAP Draft Version owned by the signed-in RD. */
  "soap_drafts:approve:own",
  /** Read the supervisor Analysis produced for a readable Session. */
  "session_analysis:read",
  /** File a session against any RD. */
  "sessions:write:all",
  /** File a session against the RDs you supervise. */
  "sessions:write:supervised",
  /** File a session against any RD that has a supervisor to review it. */
  "sessions:write:mapped",
  /** Rename, delete, retry, rerun, link, or unlink any session. */
  "sessions:manage:all",
  /** Manage sessions for the RDs you supervise. */
  "sessions:manage:supervised",
  /** GET /api/sessions/:id/raw — the raw analysis artifacts behind a run. */
  "session_artifacts:read",
  /** Read chat threads other people own, and see who owns them. */
  "chats:read:all",
  /**
   * Name the supervisor a snapshot or meeting note is filed under, instead of
   * inheriting the RD's own assignment.
   *
   * This is a WRITE decision and it has its own name for that reason: it used to
   * be asked as `rds:read:all`, which is roster REACH, so widening reach to a
   * read-only or auditor role would have handed it the ability to re-attribute
   * clinical work to an arbitrary supervisor. The two answers are identical
   * today — `admin` is the only role holding either — and the point of the split
   * is that they no longer have to stay that way by accident.
   */
  "supervisor_attribution:assign",
  /**
   * Save the practice-wide notification defaults and any RD's notification
   * defaults (NO-827). `all` is every defaults layer: the practice's and each
   * RD's. How an RD edits their own defaults is not this name's question; the
   * settings API (NO-829) decides that from the RD's own identity, and may add
   * an `own` rung beside this one.
   */
  "notification_defaults:manage:all",
  /**
   * Read and save the bookable hours and breaks of the signed-in RD's active
   * Provider (NO-867): the every-week hours, one-week changes, and time zone.
   */
  "rd_hours:manage:own",
  /**
   * Read and save the bookable hours of an RD whose supervision edge names you
   * (NO-867). Full edit, like the RD's own. It opens no Appointment read by
   * itself: the week's booked visits need `rd_hours_visits:read:supervised`.
   */
  "rd_hours:manage:supervised",
  /**
   * See the booked visits (times and Client names) the RD hours week read
   * returns for an RD whose supervision edge names you (NO-867). Each such
   * read is recorded as a supervised Clinical Read (`appointment`/`list`,
   * lane `supervised`). It also governs the RD Schedule week read
   * (`GET /api/rd-schedule/week`, NO-868) for that RD, recorded the same way.
   * It opens no other Appointment read: no Appointment list, detail, Note or
   * documentation of that RD.
   */
  "rd_hours_visits:read:supervised",
  /** Read and save the bookable hours of any RD (NO-867). */
  "rd_hours:manage:all",
  /**
   * Book, reschedule, edit, cancel or mark missed, and delete the signed-in
   * RD's own Appointments from RD Schedule (NO-872). Healthie receives each
   * write with the RD's own key.
   */
  "appointments:manage:own",
  /**
   * The same RD Schedule actions on an RD whose supervision edge names you
   * (NO-872). The write still runs with that RD's key, and the request row
   * names you as the person who asked.
   */
  "appointments:manage:supervised",
  /** The same RD Schedule actions on any RD, acting for them (NO-872). */
  "appointments:manage:all",
  /** /api/admin/rds (always-ai) — the RD registry and supervisor assignment. */
  "rd_registry:manage",
  /** /api/admin/coverage (always-ai) — the RD session-coverage dashboard. */
  "rd_coverage:read",
  /** /api/admin/usage — per-RD usage reporting. */
  "rd_usage:read",
  /** /api/admin/soap-workbench — ephemeral production model comparison. */
  "soap_prompt_workbench:use",
  /** /api/admin/scorecard-workbench — fixture-backed Scorecard candidate comparison. */
  "scorecard_workbench:use",
  /** /api/admin/starter-prompts — the managed starter-prompt library. */
  "starter_prompts:manage",
  /** /api/admin/migrate — the in-app migration runner. */
  "migrations:run",
  /** /api/dev — the non-production developer utilities. */
  "dev_tools:use",

  // --- shared --------------------------------------------------------------
  /** Administer people and their app-scoped roles through NDD Admin. */
  "staff:manage",
  /** Read `core_data.role_events` — the registry's audit trail. */
  "role_events:read",
  /** Resolve the canonical User attached to a Healthie Provider. */
  "provider_links:manage",
  /**
   * NDD Admin's Data Inspections: the Provider inspection, the records-access
   * window lifecycle, and the appointment reads those windows open.
   */
  "data_inspections:read",
  /**
   * NDD Admin's Onboard RD (NO-761): create an RD's Healthie provider and NDD
   * account with AlwaysAI `rd`, and queue their AlwaysAI sign-in account and
   * enrollment. Grants no other role and reaches no other NDD Admin page.
   */
  "providers:onboard",
  /** NDD Admin's Payroll reports (NO-727): generate a pay-period payroll report. */
  "payroll_reports:generate",
  /** NDD Admin's Payroll reports (NO-727): download a generated payroll report. */
  "payroll_reports:download",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * What booking's admin tier reaches. Spliced into `system_admin` rather than
 * restated so the two tiers cannot drift: booking's tiering is additive — a
 * system admin is an admin who also holds the two controls below — and writing
 * it as a spread is what makes that sentence true in code.
 */
const BOOKING_ADMIN_PERMISSIONS = [
  "platform_health:read",
  "state_coverage:read",
  "hubspot_directory:read",
  "schedulable_rds:manage",
  "appointment_types:manage",
  "rulesets:manage",
  "analysis_chats:use",
  "call_review:use",
] as const satisfies readonly Permission[];

/**
 * Booking's roles, TOTAL over its vocabulary: adding a value to
 * `BOOKING_USER_ROLES` without deciding what it may do is a compile error here.
 *
 * `frontdesk` holds nothing, and that is the truth rather than an omission. The
 * operator workspace has no permission gate — `createRequireBookingUser` is its
 * whole guard, so being provisioned for booking at all is what opens it — and
 * every route that DOES gate asks for one of the names below.
 */
export const BOOKING_ROLE_PERMISSIONS: Record<BookingUserRole, readonly Permission[]> =
  {
    frontdesk: [],
    admin: BOOKING_ADMIN_PERMISSIONS,
    system_admin: [
      ...BOOKING_ADMIN_PERMISSIONS,
      "reference_sync:manage",
      "telemetry_smoke:run",
    ],
  };

/**
 * An RD's own clinical work: their schedule and bookable hours, Notes, Patient
 * Quick Notes, SOAP Drafts, Session Recaps, and the Sessions their active
 * Provider owns. Every rung is `own`, so holding it without a Provider reaches
 * nothing.
 *
 * Spliced into every role whose holder sees clients, rather than granted as a
 * second `rd` row: a supervisor IS an RD who also supervises, and a clinical
 * admin is a supervisor who reaches everyone. The tiering is additive, the same
 * way booking's is, and the spread is what makes that sentence true in code.
 * The mapped upload rungs stay `rd`/`va`-only on purpose: holding
 * `sessions:write:mapped` widens the upload picker past a supervisor's roster.
 */
const RD_OWN_WORK_PERMISSIONS = [
  "sessions:read:own",
  "appointments:read:own",
  "session_recaps:read:own",
  "appointment_notes:read:own",
  "appointment_notes:write:own",
  "patient_quick_notes:read:own",
  "patient_quick_notes:write:own",
  "patient_red_flags:read:own",
  "patient_red_flags:write:own",
  "soap_drafts:read:own",
  "soap_drafts:edit:own",
  "soap_drafts:approve:own",
  "rd_hours:manage:own",
  "appointments:manage:own",
] as const satisfies readonly Permission[];

/** Every-RD reach, for both admin tiers. */
const ALWAYS_AI_ADMIN_PERMISSIONS = [
  "metrics:read:all",
  "rds:read:all",
  "rds:create:all",
  "sessions:read:all",
  "session_analysis:read",
  "sessions:write:all",
  "sessions:manage:all",
  "session_artifacts:read",
  // Read-only support/QA reach (NO-467). The only appointment write rung is
  // `appointments:manage:all` below, which reaches RD Schedule actions alone, so
  // Note saves, presence, and Enhancement stay unreachable on another RD's
  // Appointments.
  "appointments:read:all",
  // Act on any RD's schedule for them, with the RD's key (NO-872).
  "appointments:manage:all",
  "appointment_notes:read:all",
  "chats:read:all",
  "supervisor_attribution:assign",
  // Admins set practice defaults and edit any RD's; RD-tier roles never hold it.
  "notification_defaults:manage:all",
  // Admins read and save any RD's hours (NO-867).
  "rd_hours:manage:all",
  "rd_registry:manage",
  "rd_coverage:read",
  "rd_usage:read",
  "soap_prompt_workbench:use",
  "scorecard_workbench:use",
  "starter_prompts:manage",
  "migrations:run",
  "dev_tools:use",
] as const satisfies readonly Permission[];

/**
 * always-ai's roles, TOTAL over its vocabulary.
 *
 * Two admin tiers because two different people hold admin: `admin` is staff
 * with every-RD reach and no caseload (engineering), and `clinical_admin` is a
 * supervisor who reaches every RD and also sees their own clients. Both read any
 * RD's Appointments read-only and may act on any RD's schedule through RD
 * Schedule (NO-872); only the clinical tier reads and writes its own Notes.
 *
 * `rd` and `va` share mapped creation/upload permissions. `rd`, `supervisor`,
 * and `clinical_admin` hold the RD's own clinical work. VA remains upload-only.
 */
export const ALWAYS_AI_ROLE_PERMISSIONS: Record<
  AlwaysAiUserRole,
  readonly Permission[]
> = {
  admin: ALWAYS_AI_ADMIN_PERMISSIONS,
  clinical_admin: [...ALWAYS_AI_ADMIN_PERMISSIONS, ...RD_OWN_WORK_PERMISSIONS],
  supervisor: [
    "metrics:read:supervised",
    "rds:read:supervised",
    "rds:create:supervised",
    "sessions:read:supervised",
    "session_analysis:read",
    "sessions:write:supervised",
    "sessions:manage:supervised",
    "rd_hours:manage:supervised",
    "rd_hours_visits:read:supervised",
    "appointments:manage:supervised",
    ...RD_OWN_WORK_PERMISSIONS,
  ],
  rd: ["rds:create:mapped", "sessions:write:mapped", ...RD_OWN_WORK_PERMISSIONS],
  va: ["rds:create:mapped", "sessions:write:mapped"],
};

/**
 * NDD Admin's roles, TOTAL over its vocabulary.
 *
 * NDD Admin reads this map on every request (NO-762): its middleware derives the
 * signed-in user's permissions from their `app='admin'` roles, and each API
 * mount requires one of these names (`apps/ndd-admin/server/app.ts`). Holding a
 * role is therefore no longer the same as reaching every page — a role reaches
 * exactly what its entry lists, which is what makes a narrower second role safe
 * to add.
 */
export const ADMIN_ROLE_PERMISSIONS: Record<AdminUserRole, readonly Permission[]> = {
  platform_admin: [
    "staff:manage",
    "role_events:read",
    "provider_links:manage",
    "data_inspections:read",
    "providers:onboard",
  ],
  // NO-761: onboarding only. A provider onboarder reaches the Onboard RD page
  // and nothing else, so onboarding can be handed to someone who should not
  // see Staff, Audit, Provider Links or Data Inspections.
  provider_onboarder: ["providers:onboard"],
  // NO-727: payroll reports only, so the person who runs payroll sees no staff,
  // audit, provider or patient data.
  payroll: ["payroll_reports:generate", "payroll_reports:download"],
};
